/**
 * Free Shipping Bar (Padel USA custom)
 * ---------------------------------------------------------------------------
 * Progress bar toward the free-shipping threshold. Reproduces the UpCart
 * free-shipping bar for the native cart (UpCart replacement). The section
 * server-renders the initial state; this refreshes it live on the theme
 * `cart:update` event (fired by both CartAddEvent and CartUpdateEvent).
 *
 * Docs: custom-code-padelusa/features/free-shipping-bar.md
 */
const FSB_ROUTES =
  typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : { cart_url: '/cart' };

class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.standardThreshold = parseInt(this.dataset.standardThresholdCents || this.dataset.thresholdCents || '0', 10);
    this.bagThreshold = parseInt(this.dataset.bagThresholdCents || this.dataset.thresholdCents || '0', 10);
    this.bagProductTypes = (this.dataset.bagProductTypes || 'padel bag')
      .split(',')
      .map((type) => type.trim().toLowerCase())
      .filter(Boolean);
    this.threshold = parseInt(this.dataset.thresholdCents || '0', 10);
    this.msgProgress = this.dataset.msgProgress || "You're [amount] away from free shipping";
    this.msgUnlocked = this.dataset.msgUnlocked || 'Free shipping unlocked';
    this.messageEl = this.querySelector('.free-shipping-bar__message');
    this.fillEl = this.querySelector('.free-shipping-bar__fill');
    this.onCartUpdate = () => this.refresh();
    document.addEventListener('cart:update', this.onCartUpdate);
    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.onCartUpdate);
  }

  async refresh() {
    if ((!this.standardThreshold || this.standardThreshold <= 0) && (!this.bagThreshold || this.bagThreshold <= 0)) {
      this.hidden = true;
      return;
    }
    try {
      const cart = await fetch(`${FSB_ROUTES.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      this.render(cart);
    } catch (error) {
      /* keep the server-rendered state on failure */
    }
  }

  render(cart) {
    const totalCents = cart?.total_price || 0;
    const hasBag = (cart?.items || []).some((item) =>
      this.bagProductTypes.includes(String(item.product_type || '').trim().toLowerCase())
    );
    this.threshold = hasBag ? this.bagThreshold : this.standardThreshold;
    if (!this.threshold || this.threshold <= 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    const pct = Math.min(100, Math.round((totalCents / this.threshold) * 100));
    if (this.fillEl) this.fillEl.style.width = `${pct}%`;
    const remaining = Math.max(0, this.threshold - totalCents);
    const unlocked = remaining <= 0;
    this.classList.toggle('is-unlocked', unlocked);
    if (this.messageEl) {
      this.messageEl.textContent = unlocked
        ? this.msgUnlocked
        : this.msgProgress.replace('[amount]', this.money(remaining));
    }
  }

  money(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
